import 'swiper/css'

import { rankItem } from '@tanstack/match-sorter-utils'
import { createSolidTable, flexRender } from '@tanstack/solid-table'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  RowData,
  Table,
} from '@tanstack/table-core'
import { decode } from 'html-entities'
import { Component, createEffect, createMemo, createSignal, For, on, Show } from 'solid-js'

import { SafeHtml } from '@/components/SafeHtml'
import { AmicusBrief } from '@/types/AmicusBriefs'
import { cx, removeHtmlTags, shortenString } from '@/utils/utils'
import { Filter } from './AmicusTableFilter'

export type ColumnTypes = 'checkbox' | 'tags'

/* eslint-disable no-unused-vars */
declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    type?: ColumnTypes
  }
}
/* eslint-enable no-unused-vars */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value)

  // Store the itemRank info
  addMeta({
    itemRank,
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

const defaultColumns: ColumnDef<AmicusBrief>[] = [
  {
    id: 'caseYear',
    accessorFn: (row) => row.acf.case_year,
    cell: (info) => <div class="text-center">{info.getValue<number>()}</div>,
    header: () => 'Year',
  },
  {
    id: 'name',
    accessorFn: (row) => row.title.rendered,
    cell: (info) => <span>{decode(info.getValue<string>())}</span>,
    header: () => 'Case Name',
  },
  {
    accessorKey: 'summary',
    enableSorting: false,
    accessorFn: (row) => row.acf.summary,
    cell: (info) => {
      const summaryTrimmed = shortenString(removeHtmlTags(info.getValue<string>()), 150, '...')
      return <SafeHtml input={summaryTrimmed} tag="span" />
    },
    header: () => 'Case Summary',
  },
  {
    id: 'keywords',
    accessorFn: (row) => row.acf.keywords,
    enableSorting: false,
    filterFn: 'includesString',
    meta: { type: 'tags' },
    cell: (info) => (
      <div class="flex flex-wrap gap-6">
        <For each={info.renderValue<string[]>()}>
          {(keyword) => (
            <span class="inline-block whitespace-nowrap rounded bg-gray2 px-8 py-4 text-[0.75rem] leading-none text-white">
              {keyword}
            </span>
          )}
        </For>
      </div>
    ),
    header: () => 'Keywords',
  },
  {
    id: 'requestedBrief',
    enableSorting: false,
    accessorFn: (row) => row.acf.requested,
    header: () => 'Requested',
    meta: { type: 'checkbox' },
  },
  {
    accessorKey: 'link',
    cell: (info) => (
      <a href={info.getValue<string>()} class="btn-small whitespace-nowrap">
        Read Case
      </a>
    ),
    header: () => '',
    enableColumnFilter: false,
  },
]

type AmicusBriefsTableProps = {
  data: AmicusBrief[]
}

export const AmicusBriefsTable: Component<AmicusBriefsTableProps> = (props) => {
  const [data, setData] = createSignal<AmicusBrief[]>()
  const [amicusTable, setAmicusTable] = createSignal<Table<AmicusBrief>>()

  createEffect(() => {
    setData(props.data)
  })

  const [columnFilters, setColumnFilters] = createSignal<ColumnFiltersState>([])

  const columns = createMemo<ColumnDef<AmicusBrief>[]>(() => defaultColumns)

  createEffect(
    on(
      data,
      (data) => {
        if (!data) return
        const table = createSolidTable({
          get data(): AmicusBrief[] {
            return data
          },
          columns: columns(),
          getCoreRowModel: getCoreRowModel(),
          state: {
            get columnFilters() {
              return columnFilters()
            },
          },
          filterFns: {
            fuzzy: fuzzyFilter,
          },
          onColumnFiltersChange: setColumnFilters,
          getFilteredRowModel: getFilteredRowModel(),
          globalFilterFn: fuzzyFilter,
          getSortedRowModel: getSortedRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
          getFacetedMinMaxValues: getFacetedMinMaxValues(),
          debugTable: true,
        })
        setAmicusTable(table)
      },
      { defer: false }
    )
  )

  createEffect(
    on(
      amicusTable,
      (amicusTable) => {
        if (!amicusTable) return
        if (amicusTable.getState().columnFilters[0]?.id === 'caseYear') {
          if (amicusTable.getState().sorting[0]?.id !== 'caseYear') {
            amicusTable.setSorting([{ id: 'caseYear', desc: true }])
          }
        }
      },
      { defer: false }
    )
  )

  const tableStyles = cx([
    'not-prose border-collapse font-camptonMedium',
    '[&_th]:border [&_td]:border border',
    '[&_th]:border-legacy-grayAlto [&_td]:border-legacy-grayAlto border-legacy-grayAlto',
  ])
  return (
    <Show when={amicusTable()} keyed>
      {(table) => (
        <div>
          <div class="mb-20 max-md:space-y-2 md:mb-40 md:grid md:grid-cols-3 md:gap-8">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <For each={headerGroup.headers}>
                  {(header) => (
                    <Show when={!header.isPlaceholder}>
                      {header.column.getCanFilter() ? (
                        <div class="">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          :
                          <Filter
                            column={header.column}
                            table={table}
                            class="heir:font-camptonMedium"
                          />
                        </div>
                      ) : null}
                    </Show>
                  )}
                </For>
              )}
            </For>
          </div>
          <div class="max-md:overflow-x-scroll">
            <table class={tableStyles}>
              <thead>
                <For each={table.getHeaderGroups()}>
                  {(headerGroup) => (
                    <tr>
                      <For each={headerGroup.headers}>
                        {(header) => (
                          <th colSpan={header.colSpan} class="p-8">
                            <Show when={!header.isPlaceholder}>
                              <div
                                class={`font-camptonMedium text-14 font-bold ${
                                  header.column.getCanSort()
                                    ? 'cursor-pointer select-none whitespace-nowrap'
                                    : undefined
                                }`}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: ' 🔼',
                                  desc: ' 🔽',
                                }[header.column.getIsSorted() as string] ?? null}
                              </div>
                            </Show>
                          </th>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </thead>
              <tbody class="text-14 heir:leading-21">
                <For each={table.getRowModel().rows}>
                  {(row) => (
                    <tr>
                      <For each={row.getVisibleCells()}>
                        {(cell) => (
                          <td
                            class={cx([
                              'p-8',
                              cell.column.columnDef.id === 'requestedBrief' ? 'text-center' : '',
                            ])}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
              <tfoot class="hidden">
                <For each={table.getFooterGroups()}>
                  {(footerGroup) => (
                    <tr>
                      <For each={footerGroup.headers}>
                        {(header) => (
                          <th>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.footer, header.getContext())}
                          </th>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </Show>
  )
}
